/**
 * How a value finds rows: the options shared by row matching ("Zeilen
 * schreiben") and relation lookup, and the editor of a relation lookup
 * (compare mode, lookup column, several hits, already linked rows).
 */
import { useI18n } from '@affine/i18n';
import type { FC } from 'react';

import type {
  MultiMatchBehavior,
  StealBehavior,
  TargetMatchMode,
} from '../../services/automation';
import { SectionField, SectionStack } from './section-layout';
import { SettingSelect, type SettingSelectOption } from './setting-select';

// =============================================================================
// Option Constants (shared across all contexts)
// =============================================================================

/** An option of a match dropdown; label and description are i18n keys. */
export interface MatchOption<T extends string = string> {
  value: T;
  label: MatchCopyKey;
  description: MatchCopyKey;
}

type MatchCopyKey = `com.streamlain.streamlain.match.${
  | 'ask'
  | 'ask-desc'
  | 'fail'
  | 'mode-auto'
  | 'mode-auto-desc'
  | 'mode-contains'
  | 'mode-contains-desc'
  | 'mode-exact'
  | 'mode-exact-desc'
  | 'mode-starts-with'
  | 'mode-starts-with-desc'
  | 'multi-fail-desc'
  | 'multi-skip'
  | 'multi-skip-desc'
  | 'multi-update-all'
  | 'multi-update-all-desc'
  | 'multi-update-first'
  | 'multi-update-first-desc'
  | 'steal-fail-desc'
  | 'steal-silent'
  | 'steal-silent-desc'}`;

/**
 * Match mode options with 'auto' — for row matching.
 */
export const MATCH_MODE_WITH_AUTO_OPTIONS: Array<MatchOption<TargetMatchMode>> =
  [
    {
      value: 'auto',
      label: 'com.streamlain.streamlain.match.mode-auto',
      description: 'com.streamlain.streamlain.match.mode-auto-desc',
    },
    {
      value: 'exact',
      label: 'com.streamlain.streamlain.match.mode-exact',
      description: 'com.streamlain.streamlain.match.mode-exact-desc',
    },
    {
      value: 'contains',
      label: 'com.streamlain.streamlain.match.mode-contains',
      description: 'com.streamlain.streamlain.match.mode-contains-desc',
    },
    {
      value: 'starts_with',
      label: 'com.streamlain.streamlain.match.mode-starts-with',
      description: 'com.streamlain.streamlain.match.mode-starts-with-desc',
    },
  ];

/**
 * Multi-match behavior options.
 */
export const MULTI_MATCH_BEHAVIOR_OPTIONS: Array<
  MatchOption<MultiMatchBehavior>
> = [
  {
    value: 'fail',
    label: 'com.streamlain.streamlain.match.fail',
    description: 'com.streamlain.streamlain.match.multi-fail-desc',
  },
  {
    value: 'update_all',
    label: 'com.streamlain.streamlain.match.multi-update-all',
    description: 'com.streamlain.streamlain.match.multi-update-all-desc',
  },
  {
    value: 'update_first',
    label: 'com.streamlain.streamlain.match.multi-update-first',
    description: 'com.streamlain.streamlain.match.multi-update-first-desc',
  },
  {
    value: 'skip',
    label: 'com.streamlain.streamlain.match.multi-skip',
    description: 'com.streamlain.streamlain.match.multi-skip-desc',
  },
  {
    value: 'ask',
    label: 'com.streamlain.streamlain.match.ask',
    description: 'com.streamlain.streamlain.match.ask-desc',
  },
];

/**
 * sl-tah4.4: Steal behavior options (for relations with inverseCardinality='one').
 */
export const STEAL_BEHAVIOR_OPTIONS: Array<MatchOption<StealBehavior>> = [
  {
    value: 'fail',
    label: 'com.streamlain.streamlain.match.fail',
    description: 'com.streamlain.streamlain.match.steal-fail-desc',
  },
  {
    value: 'silent_steal',
    label: 'com.streamlain.streamlain.match.steal-silent',
    description: 'com.streamlain.streamlain.match.steal-silent-desc',
  },
  {
    value: 'ask',
    label: 'com.streamlain.streamlain.match.ask',
    description: 'com.streamlain.streamlain.match.ask-desc',
  },
];

/** The options with their translated label and description. */
export function useMatchOptions<T extends string>(
  options: ReadonlyArray<MatchOption<T>>
): SettingSelectOption<T>[] {
  const t = useI18n();
  return options.map(option => ({
    value: option.value,
    label: t[option.label](),
    description: t[option.description](),
  }));
}

interface ColumnOption {
  id: string;
  name: string;
  type: string;
}

/** Column types a text value can be looked up in. */
const LOOKUP_COLUMN_TYPES = ['title', 'rich-text', 'text', 'number', 'select'];
const TITLE_COLUMN = 'title';

export interface MatchConfigEditorProps {
  matchMode: string;
  matchModeOptions: ReadonlyArray<MatchOption>;
  onMatchModeChange: (mode: string) => void;
  multiMatchBehavior: MultiMatchBehavior;
  onMultiMatchBehaviorChange: (behavior: MultiMatchBehavior) => void;
  /** Columns of the linked table; the title column is the default. */
  columns?: ColumnOption[];
  selectedColumnId?: string;
  onColumnChange: (columnId: string | undefined) => void;
  /** Only for relations whose linked rows accept one link (1:1, N:1). */
  stealBehavior?: StealBehavior;
  onStealBehaviorChange?: (behavior: StealBehavior) => void;
}

export const MatchConfigEditor: FC<MatchConfigEditorProps> = ({
  matchMode,
  matchModeOptions,
  onMatchModeChange,
  multiMatchBehavior,
  onMultiMatchBehaviorChange,
  columns = [],
  selectedColumnId,
  onColumnChange,
  stealBehavior,
  onStealBehaviorChange,
}) => {
  const t = useI18n();
  const modeOptions = useMatchOptions(matchModeOptions);
  const multiOptions = useMatchOptions(MULTI_MATCH_BEHAVIOR_OPTIONS);
  const stealOptions = useMatchOptions(STEAL_BEHAVIOR_OPTIONS);
  const columnOptions = columns
    .filter(column => LOOKUP_COLUMN_TYPES.includes(column.type))
    .map(column => ({
      value: column.type === 'title' ? TITLE_COLUMN : column.id,
      label:
        column.type === 'title'
          ? t['com.streamlain.streamlain.match.column-title-default']()
          : column.name,
    }));
  const matchLabel = t['com.streamlain.streamlain.match.label-match']();
  const columnLabel = t['com.streamlain.streamlain.match.label-in-column']();
  const multipleLabel = t['com.streamlain.streamlain.match.label-multiple']();
  const linkedLabel = t['com.streamlain.streamlain.match.label-linked']();

  return (
    <SectionStack>
      {columnOptions.length > 0 && (
        <SectionField label={columnLabel}>
          <SettingSelect
            value={selectedColumnId ?? TITLE_COLUMN}
            options={columnOptions}
            onChange={columnId =>
              onColumnChange(columnId === TITLE_COLUMN ? undefined : columnId)
            }
            ariaLabel={columnLabel}
          />
        </SectionField>
      )}
      <SectionField label={matchLabel}>
        <SettingSelect
          value={matchMode}
          options={modeOptions}
          onChange={onMatchModeChange}
          ariaLabel={matchLabel}
        />
      </SectionField>
      <SectionField label={multipleLabel}>
        <SettingSelect
          value={multiMatchBehavior}
          options={multiOptions}
          onChange={onMultiMatchBehaviorChange}
          ariaLabel={multipleLabel}
        />
      </SectionField>
      {stealBehavior && onStealBehaviorChange && (
        <SectionField label={linkedLabel}>
          <SettingSelect
            value={stealBehavior}
            options={stealOptions}
            onChange={onStealBehaviorChange}
            ariaLabel={linkedLabel}
          />
        </SectionField>
      )}
    </SectionStack>
  );
};
